/**
 * Devello invoice HTML for html2canvas + jsPDF.
 * Logos: data URLs paint reliably; remote URLs are inlined when CORS allows.
 */

import poweredByMark from '../ChatGPT Image Jun 26, 2025, 12_35_25 AM.png';
import { devWarn } from './logger';

/** Screen-preview only — exported PDFs stay light/printable. */
function applyInvoiceDarkPreviewTheme(html) {
  return html
    .replace(/background:#ffffff/gi, 'background:#1e293b')
    .replace(/background:#fff\b/gi, 'background:#1e293b')
    .replace(/color:#0f172a/gi, 'color:#f1f5f9')
    .replace(/color:#334155/gi, 'color:#cbd5e1')
    .replace(/color:#475569/gi, 'color:#94a3b8')
    .replace(/color:#64748b/gi, 'color:#94a3b8')
    .replace(/color:#94a3b8/gi, 'color:#64748b')
    .replace(/background:#f1f5f9/gi, 'background:#334155')
    .replace(/border-bottom:1px solid #f1f5f9/gi, 'border-bottom:1px solid #475569')
    .replace(/border-bottom:1px solid #e2e8f0/gi, 'border-bottom:1px solid #475569')
    .replace(/border:1px solid #e2e8f0/gi, 'border:1px solid #475569')
    .replace(/border-bottom:2px solid #cbd5e1/gi, 'border-bottom:2px solid #64748b')
    .replace(/background:linear-gradient\(135deg,#[0-9a-fA-F]{6} 0%,#ffffff 100%\)/gi, 'background:linear-gradient(135deg,#334155 0%,#1e293b 100%)')
    .replace(/background:linear-gradient\(135deg,#f0fdfa 0%,#ffffff 100%\)/gi, 'background:linear-gradient(135deg,#334155 0%,#1e293b 100%)')
    .replace(/border-top:1px dashed #cbd5e1/gi, 'border-top:1px dashed #64748b')
    .replace(/border-top:1px solid #d1d5db/gi, 'border-top:1px solid #64748b')
    .replace(/border-bottom:1px solid #d1d5db/gi, 'border-bottom:1px solid #64748b');
}

function escapeHtml(text) {
  if (text == null || text === '') return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Detail text under line title; newlines → <br> */
function escapeDetailHtml(text) {
  if (text == null || String(text).trim() === '') return '';
  return escapeHtml(String(text)).replace(/\n/g, '<br/>');
}

/** Default accent when Pro has no saved color (neutral slate). */
export const DEFAULT_PRO_INVOICE_ACCENT = '#64748b';

const LEGACY_TEAL_STYLES = {
  barGradient: 'linear-gradient(90deg,#0d9488 0%,#1761A0 55%,#2ED8C3 100%)',
  titleColor: '#0d9488',
  billBorder: '#14b8a6',
  detailBorder: '#ccfbf1',
  detailBg: 'linear-gradient(135deg,#f0fdfa 0%,#ffffff 100%)',
  detailLabel: '#0f766e',
};

function clamp255(n) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function parseHexColor(input) {
  if (input == null || typeof input !== 'string') return null;
  let h = input.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (h.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function rgbToHex(r, g, b) {
  return `#${[r, g, b]
    .map((x) => clamp255(x).toString(16).padStart(2, '0'))
    .join('')}`;
}

function mixRgb(a, b, t) {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ];
}

/**
 * Derive PDF accent styles from a single brand hex (Pro-customizable).
 */
export function deriveInvoiceAccentStyles(accentHex) {
  const rgb = parseHexColor(accentHex);
  if (!rgb) {
    return deriveInvoiceAccentStyles(DEFAULT_PRO_INVOICE_ACCENT);
  }
  const [r, g, b] = rgb;
  const white = [255, 255, 255];
  const black = [15, 23, 42];
  const barStart = rgbToHex(...mixRgb(rgb, black, 0.14));
  const barMid = rgbToHex(...rgb);
  const barEnd = rgbToHex(...mixRgb(rgb, white, 0.38));
  const barGradient = `linear-gradient(90deg,${barStart} 0%,${barMid} 52%,${barEnd} 100%)`;
  const titleColor = rgbToHex(...rgb);
  const billBorder = rgbToHex(...mixRgb(rgb, black, 0.1));
  const detailBorder = `rgba(${r},${g},${b},0.38)`;
  const bgTint = rgbToHex(...mixRgb(rgb, white, 0.93));
  const detailBg = `linear-gradient(135deg,${bgTint} 0%,#ffffff 100%)`;
  const detailLabel = rgbToHex(...mixRgb(rgb, black, 0.42));
  return { barGradient, titleColor, billBorder, detailBorder, detailBg, detailLabel };
}

/** First column: bold title (description) + optional smaller detail below */
function lineItemDescriptionCellHtml(item) {
  const title = String(item.description ?? item.name ?? '').trim();
  const detailRaw = item.detail != null ? String(item.detail).trim() : '';
  const titleEsc = escapeHtml(title);
  const detailEsc = escapeDetailHtml(detailRaw);

  if (!title && !detailRaw) return '—';
  if (!detailRaw) {
    return `<div style="font-weight:600;color:#0f172a;font-size:13px;line-height:1.35;">${titleEsc}</div>`;
  }
  if (!title) {
    return `<div style="font-size:12px;color:#64748b;line-height:1.45;">${detailEsc}</div>`;
  }
  return `<div><div style="font-weight:600;color:#0f172a;font-size:13px;line-height:1.35;">${titleEsc}</div><div style="font-size:12px;color:#64748b;line-height:1.45;margin-top:4px;">${detailEsc}</div></div>`;
}

function formatMoney(n) {
  const v = typeof n === 'number' ? n : parseFloat(n) || 0;
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatLongDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return escapeHtml(String(iso));
    return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return escapeHtml(String(iso));
  }
}

/** Lines for Notes block — supports notesSection or legacy headerSection */
export function getInvoiceNotesLines(invoiceData) {
  const flat = invoiceData || {};
  const ns = flat.notesSection;
  if (ns && Array.isArray(ns.lines)) {
    return ns.lines.filter((l) => l && String(l.text || '').trim());
  }
  const hs = flat.headerSection;
  if (!hs || !hs.show) return [];
  const lines = [];
  if (hs.title && String(hs.title).trim()) lines.push({ text: hs.title });
  if (Array.isArray(hs.bulletPoints)) {
    hs.bulletPoints.forEach((p) => {
      if (p && String(p.text || '').trim()) lines.push({ text: p.text });
    });
  }
  return lines;
}

/**
 * Inline remote or blob URLs to data URLs so html2canvas can paint them.
 */
export async function resolveImageUrlForCanvas(src) {
  if (!src || typeof src !== 'string') return '';
  const trimmed = src.trim();
  if (trimmed.startsWith('data:image')) return trimmed;

  try {
    const response = await fetch(trimmed, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => reject(new Error('read failed'));
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    devWarn('[invoice PDF] Could not inline logo:', e?.message || e);
    return '';
  }
}

function normalizeCompanyInfo(invoiceData) {
  const flat = invoiceData || {};
  const c = flat.companyInfo || {};
  const logoSrc = c.logoUrl || c.companyLogo || flat.logoUrl || flat.companyLogo || '';
  return {
    name: c.name || flat.companyName || 'Your Company',
    streetAddress: c.streetAddress || flat.streetAddress || '',
    city: c.city || flat.city || '',
    province: c.province || flat.province || '',
    postalCode: c.postalCode || flat.postalCode || '',
    phone: c.phone || flat.companyPhone || '',
    email: c.email || flat.companyEmail || '',
    hstNumber: c.hstNumber || flat.companyHstNumber || flat.hstNumber || '',
    logoUrl: logoSrc,
  };
}

function normalizeClientInfo(invoiceData) {
  const flat = invoiceData || {};
  const cl = flat.clientInfo || {};
  return {
    name: cl.name || flat.clientName || 'Client',
    street: cl.street || flat.clientAddress || '',
    city: cl.city || flat.clientCity || '',
    province: cl.province || flat.clientProvince || '',
    postalCode: cl.postalCode || flat.clientPostalCode || '',
  };
}

/**
 * Subtotal, tax, and grand total exactly as rendered on the invoice PDF.
 * Use this for email "amount due" so it never drifts from the attached PDF.
 */
export function computeInvoiceDocumentAmounts(invoiceData) {
  const flat = invoiceData || {};
  /** Manual invoice amount (before tax); line items can be descriptive only (no per-line math). */
  if (flat.manualTotalEnabled === true) {
    const mt = parseFloat(flat.manualTotal);
    const subtotal = Number.isFinite(mt) ? mt : 0;
    const showTax = flat.taxEnabled !== false;
    if (!showTax) {
      return { subtotal, showTax: false, hstAmount: 0, total: subtotal };
    }
    const hstAmountRaw = parseFloat(flat.hst ?? flat.hstAmount ?? 0) || 0;
    const hstAmount = hstAmountRaw;
    const totalFromData = parseFloat(flat.total);
    const total = Number.isFinite(totalFromData) ? totalFromData : subtotal + hstAmount;
    return { subtotal, showTax: true, hstAmount, total };
  }
  const subtotal = parseFloat(flat.subtotal) || 0;
  const showTax = flat.taxEnabled !== false;
  const hstAmountRaw = parseFloat(flat.hst ?? flat.hstAmount ?? 0) || 0;
  const hstAmount = showTax ? hstAmountRaw : 0;
  const totalFromData = parseFloat(flat.total);
  const total = showTax
    ? (Number.isFinite(totalFromData) ? totalFromData : subtotal + hstAmount)
    : subtotal;
  return { subtotal, showTax, hstAmount, total };
}

/** Fixed column widths (px) so table amounts line up with totals & balance due */
const COL_QTY = 52;
const COL_RATE = 92;
const COL_AMOUNT = 118;

/**
 * Per-column visibility (PDF + preview). Falls back from legacy `showQtyUnit`.
 */
export function resolveInvoiceColumnVisibility(invoiceData) {
  const flat = invoiceData || {};
  const hasNew =
    flat.showInvoiceColQty !== undefined ||
    flat.showInvoiceColRate !== undefined ||
    flat.showInvoiceColAmount !== undefined;
  if (hasNew) {
    return {
      qty: flat.showInvoiceColQty !== false,
      rate: flat.showInvoiceColRate !== false,
      amount: flat.showInvoiceColAmount !== false,
    };
  }
  const legacy = flat.showQtyUnit !== false;
  return { qty: legacy, rate: legacy, amount: true };
}

/** Square logo area (px) — larger like common invoice UIs so the header left side fills space above Bill to */
const LOGO_BOX_PX = 168;

/**
 * Single-page invoice layout (Devello styling — not a clone of third-party templates).
 * @param {object} invoiceData
 * @param {string} logoDataUrl — resolved data URL or empty
 * @param {{ includeBrandingFooter?: boolean; accentColor?: string; previewTheme?: 'light' | 'dark' }} [options] — Starter: branded footer + legacy teal. Pro: omit footer; pass accentColor or invoiceData.invoiceAccentColor (defaults to neutral). previewTheme dark is for on-screen preview only.
 */
export function buildInvoiceDocumentHtml(invoiceData, logoDataUrl, options = {}) {
  const { includeBrandingFooter = true, accentColor: accentOption, previewTheme = 'light' } = options;

  const fromOption =
    typeof accentOption === 'string' && /^#[0-9A-Fa-f]{6}$/.test(accentOption.trim())
      ? accentOption.trim()
      : null;
  const fromData =
    typeof invoiceData?.invoiceAccentColor === 'string' &&
    /^#[0-9A-Fa-f]{6}$/.test(invoiceData.invoiceAccentColor.trim())
      ? invoiceData.invoiceAccentColor.trim()
      : null;
  const resolvedAccent = fromOption || fromData;

  const styles =
    includeBrandingFooter && !resolvedAccent
      ? LEGACY_TEAL_STYLES
      : deriveInvoiceAccentStyles(resolvedAccent || DEFAULT_PRO_INVOICE_ACCENT);
  const company = normalizeCompanyInfo(invoiceData);
  const client = normalizeClientInfo(invoiceData);
  const { qty: showColQty, rate: showColRate, amount: showColAmount } = resolveInvoiceColumnVisibility(invoiceData);

  const invNum = invoiceData.invoiceNumber || invoiceData.documentNumber || '—';
  const issue = formatLongDate(invoiceData.issueDate);
  const dueRaw = invoiceData.dueDate;
  const due = dueRaw ? formatLongDate(dueRaw) : 'Upon receipt';

  const lineItems = Array.isArray(invoiceData.lineItems) ? invoiceData.lineItems : [];
  const { subtotal, showTax, hstAmount, total } = computeInvoiceDocumentAmounts(invoiceData);
  const manualLineAmounts = !!invoiceData.manualTotalEnabled;
  const useLineMultiply = showColQty && showColRate && !manualLineAmounts;
  const hpRaw = invoiceData.hstPercent;
  const hstPercentParsed =
    hpRaw != null && hpRaw !== '' && !Number.isNaN(parseFloat(hpRaw)) ? parseFloat(hpRaw) : null;

  const taxLabel =
    hstPercentParsed != null && !Number.isNaN(hstPercentParsed)
      ? `Tax (${hstPercentParsed}%)`
      : 'Tax';

  const addrLine2 = [company.city, company.province, company.postalCode].filter(Boolean).join(', ');
  const companyLines = [company.streetAddress, addrLine2].filter(Boolean);

  const contactBits = [company.phone, company.email].filter(Boolean);
  const showHstNumberOnPdf = invoiceData.showHstNumberOnPdf !== false;
  const hstNoDisplay =
    showHstNumberOnPdf && String(company.hstNumber || '').trim()
      ? `HST No. ${String(company.hstNumber).trim()}`
      : '';

  const clientLines = [
    client.street,
    [client.city, client.province, client.postalCode].filter(Boolean).join(', '),
  ].filter(Boolean);

  const logoSrc = logoDataUrl || '';
  const hasLogo = !!(logoSrc && String(logoSrc).trim());
  const logoInner = hasLogo
    ? `<img src="${logoSrc}" alt="" style="max-width:${LOGO_BOX_PX}px;max-height:${LOGO_BOX_PX}px;width:auto;height:auto;object-fit:contain;display:block;" />`
    : '';

  const docTitleRaw = invoiceData.documentTitle != null ? String(invoiceData.documentTitle) : 'Invoice';
  const docTitle = escapeHtml(docTitleRaw.trim() || 'Invoice');
  const L = invoiceData.columnLabels || {};
  const colDesc = escapeHtml((L.description != null && String(L.description).trim()) ? String(L.description).trim() : 'Description');
  const colQty = escapeHtml((L.qty != null && String(L.qty).trim()) ? String(L.qty).trim() : 'Qty');
  const colRate = escapeHtml((L.rate != null && String(L.rate).trim()) ? String(L.rate).trim() : 'Rate');
  const colAmount = escapeHtml((L.amount != null && String(L.amount).trim()) ? String(L.amount).trim() : 'Amount');

  const colParts = ['<col />'];
  if (showColQty) colParts.push(`<col style="width:${COL_QTY}px" />`);
  if (showColRate) colParts.push(`<col style="width:${COL_RATE}px" />`);
  if (showColAmount) colParts.push(`<col style="width:${COL_AMOUNT}px" />`);
  const colgroupHtml = `<colgroup>${colParts.join('')}</colgroup>`;
  const colCount = colParts.length;

  const headParts = [
    `<th style="text-align:left;padding:10px 12px;font-size:11px;font-weight:700;color:#334155;text-transform:uppercase;letter-spacing:0.06em;">${colDesc}</th>`,
  ];
  if (showColQty) {
    headParts.push(
      `<th style="text-align:center;padding:10px 6px;font-size:11px;font-weight:700;color:#334155;text-transform:uppercase;letter-spacing:0.06em;">${colQty}</th>`
    );
  }
  if (showColRate) {
    headParts.push(
      `<th style="text-align:center;padding:10px 6px;font-size:11px;font-weight:700;color:#334155;text-transform:uppercase;letter-spacing:0.06em;">${colRate}</th>`
    );
  }
  if (showColAmount) {
    headParts.push(
      `<th style="text-align:center;padding:10px 6px;font-size:11px;font-weight:700;color:#334155;text-transform:uppercase;letter-spacing:0.06em;">${colAmount}</th>`
    );
  }
  const headCells = headParts.join('');

  const emptyManual = manualLineAmounts ? '&nbsp;' : '';

  const rowsHtml =
    lineItems.length === 0
      ? `<tr><td colspan="${colCount}" style="padding:24px 14px;text-align:center;color:#64748b;font-size:13px;border-bottom:1px solid #e2e8f0;">No line items.</td></tr>`
      : lineItems
          .map((item) => {
            const descCell = lineItemDescriptionCellHtml(item);
            const qty = item.quantity ?? item.qty ?? 1;
            const unit = parseFloat(item.unitPrice ?? item.price ?? 0) || 0;
            const multiplied = qty * unit;
            const rowTotal =
              item.total != null && item.total !== ''
                ? parseFloat(item.total) || 0
                : useLineMultiply
                  ? multiplied
                  : parseFloat(item.total ?? item.unitPrice ?? 0) || 0;

            const cells = [
              `<td style="padding:10px 12px;border-bottom:1px solid #e2e8f0;font-size:13px;color:#0f172a;vertical-align:top;">${descCell}</td>`,
            ];
            if (showColQty) {
              cells.push(
                `<td style="padding:10px 6px;border-bottom:1px solid #e2e8f0;font-size:13px;color:#334155;text-align:center;font-variant-numeric:tabular-nums;vertical-align:top;">${manualLineAmounts ? emptyManual : escapeHtml(String(qty))}</td>`
              );
            }
            if (showColRate) {
              cells.push(
                `<td style="padding:10px 6px;border-bottom:1px solid #e2e8f0;font-size:13px;color:#334155;text-align:center;font-variant-numeric:tabular-nums;vertical-align:top;">${manualLineAmounts ? emptyManual : `$${formatMoney(unit)}`}</td>`
              );
            }
            if (showColAmount) {
              const amtDisplay = manualLineAmounts
                ? emptyManual
                : useLineMultiply
                  ? `$${formatMoney(multiplied)}`
                  : `$${formatMoney(rowTotal)}`;
              cells.push(
                `<td style="padding:10px 6px;border-bottom:1px solid #e2e8f0;font-size:13px;color:#0f172a;text-align:center;font-weight:600;font-variant-numeric:tabular-nums;vertical-align:top;">${amtDisplay}</td>`
              );
            }
            return `<tr>${cells.join('')}</tr>`;
          })
          .join('');

  const noteLines = getInvoiceNotesLines(invoiceData);
  const hasNotes = noteLines.length > 0;
  const waveLineGrey = '#d1d5db';
  /** Right-aligned summary rules — same width as Wave’s totals block (not full page, not a sliver). */
  const WAVE_SUMMARY_RULE_MAX = '268px';
  const waveRuleTopRow = `<tr><td colspan="${colCount}" style="padding:4px 12px 0 0;border:none;text-align:right;vertical-align:top;"><div style="display:block;width:100%;max-width:${WAVE_SUMMARY_RULE_MAX};margin-left:auto;border-top:1px solid ${waveLineGrey};height:1px;overflow:hidden;line-height:0;font-size:0;">&nbsp;</div></td></tr>`;
  const waveRuleBottomRow = `<tr><td colspan="${colCount}" style="padding:2px 12px 6px 0;border:none;text-align:right;vertical-align:top;"><div style="display:block;width:100%;max-width:${WAVE_SUMMARY_RULE_MAX};margin-left:auto;border-bottom:1px solid ${waveLineGrey};height:1px;overflow:hidden;line-height:0;font-size:0;">&nbsp;</div></td></tr>`;

  const labelColspan = showColAmount && colCount > 1 ? colCount - 1 : 0;
  const taxLabelEsc = escapeHtml(taxLabel);
  const taxLabelWithColon = /:\s*$/.test(String(taxLabel || '').trim()) ? taxLabelEsc : `${taxLabelEsc}:`;

  const amtPad = 'padding-left:6px;padding-right:6px;';
  const labelCell = (extra = '') =>
    `colspan="${labelColspan}" style="text-align:right;${amtPad}vertical-align:baseline;border:none;${extra}"`;
  const amtCell = (extra = '') =>
    `style="text-align:center;${amtPad}font-variant-numeric:tabular-nums;vertical-align:baseline;border:none;${extra}"`;

  let totalsTbody = '';
  if (showTax) {
    if (showColAmount && labelColspan > 0) {
      totalsTbody += `<tr>
    <td ${labelCell('padding-top:4px;padding-bottom:6px;font-size:13px;color:#64748b;')}>Subtotal:</td>
    <td ${amtCell('padding-top:4px;padding-bottom:6px;font-size:13px;color:#0f172a;font-weight:500;')}>$${formatMoney(subtotal)}</td>
  </tr>
  <tr>
    <td ${labelCell('padding-top:6px;padding-bottom:6px;font-size:13px;color:#64748b;')}>${taxLabelWithColon}</td>
    <td ${amtCell('padding-top:6px;padding-bottom:6px;font-size:13px;color:#0f172a;font-weight:500;')}>$${formatMoney(hstAmount)}</td>
  </tr>
  ${waveRuleTopRow}`;
    } else {
      totalsTbody += `<tr><td colspan="${colCount}" style="border:none;text-align:right;padding:4px 12px 6px 0;font-size:13px;color:#64748b;">Subtotal: <span style="color:#0f172a;font-weight:500;font-variant-numeric:tabular-nums;">$${formatMoney(subtotal)}</span></td></tr>`;
      totalsTbody += `<tr><td colspan="${colCount}" style="border:none;text-align:right;padding:6px 12px 6px 0;font-size:13px;color:#64748b;">${taxLabelWithColon} <span style="color:#0f172a;font-weight:500;font-variant-numeric:tabular-nums;">$${formatMoney(hstAmount)}</span></td></tr>`;
      totalsTbody += waveRuleTopRow;
    }
  }

  const totalTopPad = showTax ? '6px' : '2px';
  if (showColAmount && labelColspan > 0) {
    totalsTbody += `<tr>
    <td ${labelCell(`padding-top:${totalTopPad};padding-bottom:6px;font-size:15px;font-weight:800;color:#0f172a;`)}">Total:</td>
    <td ${amtCell(`padding-top:${totalTopPad};padding-bottom:6px;font-size:15px;font-weight:800;color:#0f172a;`)}">$${formatMoney(total)}</td>
  </tr>`;
  } else {
    totalsTbody += `<tr><td colspan="${colCount}" style="border:none;text-align:right;padding-top:${totalTopPad};padding-bottom:6px;padding-right:12px;font-size:15px;font-weight:800;color:#0f172a;">Total: <span style="margin-left:10px;font-variant-numeric:tabular-nums;">$${formatMoney(total)}</span></td></tr>`;
  }

  if (hasNotes) {
    totalsTbody += waveRuleBottomRow;
  }

  const totalsBlockHtml = `
  <table style="width:100%;border-collapse:collapse;margin:0;border:none;table-layout:fixed;">
    ${colgroupHtml}
    <tbody>${totalsTbody}</tbody>
  </table>`;

  const notesHtml =
    hasNotes
      ? `
    <div style="margin-top:0;padding-top:6px;">
      <div style="font-size:12px;font-weight:700;color:#0f172a;margin-bottom:12px;">Notes</div>
      <div style="font-size:13px;color:#334155;line-height:1.6;">
        ${noteLines
          .map(
            (l, i) =>
              `<div style="padding:8px 0;${i < noteLines.length - 1 ? 'border-bottom:1px solid #f1f5f9;' : ''}">${escapeHtml(l.text)}</div>`
          )
          .join('')}
      </div>
    </div>`
      : '';

  const brandImgSrc = poweredByMark;

  const outerBoxStyle = includeBrandingFooter
    ? 'box-sizing:border-box;width:720px;max-width:720px;min-height:1020px;margin:0 auto;padding:0;background:#ffffff;color:#0f172a;font-family:\'Inter\',-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,sans-serif;line-height:1.5;display:flex;flex-direction:column;'
    : 'box-sizing:border-box;width:720px;max-width:720px;margin:0 auto;padding:0;background:#ffffff;color:#0f172a;font-family:\'Inter\',-apple-system,BlinkMacSystemFont,\'Segoe UI\',Roboto,sans-serif;line-height:1.5;display:block;';

  const innerWrapStyle = includeBrandingFooter
    ? 'padding:0 32px;flex:1 1 auto;display:flex;flex-direction:column;min-height:0;'
    : 'padding:0 32px;display:block;';

  const footerHtml = includeBrandingFooter
    ? `
  <div style="margin-top:auto;flex-shrink:0;padding-top:24px;padding-bottom:12px;border-top:1px dashed #cbd5e1;text-align:center;">
    <div style="display:inline-flex;align-items:center;justify-content:center;gap:8px;flex-wrap:wrap;">
      <img src="${brandImgSrc}" alt="" width="32" height="32" style="width:32px;height:32px;object-fit:contain;display:block;flex-shrink:0;margin:0;" />
      <span style="font-size:18px;color:#475569;font-weight:800;letter-spacing:0.02em;line-height:1;display:flex;align-items:center;">Powered by Devello Tech</span>
    </div>
  </div>`
    : '';

  const companyBlockHtml = `
      <div style="font-size:18px;font-weight:800;color:#0f172a;letter-spacing:-0.02em;margin:0 0 10px 0;line-height:1.25;">${escapeHtml(company.name)}</div>
      <div style="font-size:12px;color:#64748b;line-height:1.6;">
        ${companyLines.map((l) => `<div>${escapeHtml(l)}</div>`).join('')}
        ${contactBits.map((b) => `<div>${escapeHtml(b)}</div>`).join('')}
        ${hstNoDisplay ? `<div>${escapeHtml(hstNoDisplay)}</div>` : ''}
      </div>
  `.trim();

  const headerHtml = hasLogo
    ? `
  <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:28px;margin-bottom:0;">
    <div style="flex-shrink:0;display:flex;align-items:flex-start;justify-content:flex-start;">
      ${logoInner}
    </div>
    <div style="text-align:right;max-width:360px;flex:1;min-width:0;padding-top:4px;">
      <div style="font-size:17px;font-weight:700;color:${styles.titleColor};margin-bottom:6px;letter-spacing:0.02em;">${docTitle}</div>
      ${companyBlockHtml}
    </div>
  </div>
  `.trim()
    : `
  <div style="display:flex;justify-content:space-between;align-items:center;gap:28px;margin-bottom:0;">
    <div style="flex:1;min-width:0;display:flex;align-items:center;">
      <div style="font-size:40px;font-weight:900;letter-spacing:0.06em;line-height:1;color:${styles.titleColor};text-transform:uppercase;white-space:nowrap;">${docTitle}</div>
    </div>
    <div style="width:320px;flex-shrink:0;text-align:right;">
      ${companyBlockHtml}
    </div>
  </div>
  `.trim();

  const html = `
<div style="${outerBoxStyle}">
  <div style="height:5px;background:${styles.barGradient};border-radius:0 0 3px 3px;margin-bottom:24px;flex-shrink:0;"></div>
  <div style="${innerWrapStyle}">
  ${headerHtml}

  <div style="height:1px;background:#e2e8f0;margin:18px 0 18px 0;"></div>

  <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:20px;margin-bottom:22px;">
    <div style="flex:1;min-width:0;border-left:4px solid ${styles.billBorder};padding-left:14px;align-self:flex-start;">
      <div style="font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:4px;">Bill to</div>
      <div style="font-size:15px;font-weight:700;color:#0f172a;margin-bottom:4px;">${escapeHtml(client.name)}</div>
      <div style="font-size:12px;color:#475569;line-height:1.45;">
        ${clientLines.map((l) => `<div>${escapeHtml(l)}</div>`).join('')}
      </div>
    </div>
    <div style="width:220px;flex-shrink:0;align-self:flex-start;">
      <div style="border:1px solid ${styles.detailBorder};background:${styles.detailBg};border-radius:8px;padding:10px 12px;">
        <div style="font-size:10px;color:${styles.detailLabel};font-weight:700;margin-bottom:6px;text-transform:uppercase;letter-spacing:0.06em;">Invoice details</div>
        <div style="font-size:11px;color:#475569;margin-bottom:4px;line-height:1.35;"><span style="color:#94a3b8;">Number</span><br /><strong style="color:#0f172a;">${escapeHtml(String(invNum))}</strong></div>
        <div style="font-size:11px;color:#475569;margin-bottom:4px;line-height:1.35;"><span style="color:#94a3b8;">Date</span><br /><strong style="color:#0f172a;">${issue}</strong></div>
        <div style="font-size:11px;color:#475569;margin-bottom:0;line-height:1.35;"><span style="color:#94a3b8;">Due</span><br /><strong style="color:#0f172a;">${due}</strong></div>
      </div>
    </div>
  </div>

  <table style="width:100%;border-collapse:collapse;margin-bottom:2px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;table-layout:fixed;">
    ${colgroupHtml}
    <thead>
      <tr style="background:#f1f5f9;border-bottom:2px solid #cbd5e1;">
        ${headCells}
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
  </table>

  ${totalsBlockHtml}

  ${notesHtml}
  ${footerHtml}
  </div>
</div>
`.trim();

  if (previewTheme === 'dark') {
    return applyInvoiceDarkPreviewTheme(html);
  }
  return html;
}

/** @deprecated use buildInvoiceDocumentHtml */
export function buildWaveStyleInvoiceHtml(invoiceData, logoDataUrl) {
  return buildInvoiceDocumentHtml(invoiceData, logoDataUrl);
}

/**
 * Async: resolve logo then build HTML.
 */
export async function buildInvoicePdfHtmlDocument(invoiceData) {
  const company = normalizeCompanyInfo(invoiceData);
  const raw = company.logoUrl;
  const embedded =
    raw && String(raw).startsWith('data:') ? raw : await resolveImageUrlForCanvas(raw);
  const logo = embedded || '';
  return buildInvoiceDocumentHtml(invoiceData, logo, {
    includeBrandingFooter: false,
    accentColor: invoiceData.invoiceAccentColor,
  });
}

/** Wait for images inside a detached DOM node (for html2canvas). */
export function waitForImagesInElement(el) {
  if (!el) return Promise.resolve();
  const imgs = [...el.querySelectorAll('img')];
  return Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalHeight > 0) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
    )
  );
}
